import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Query Sub Source master (Masters → Query Sub Sources). A finer breakdown of a
 * Query Source (e.g. source "referral" → sub source "existing_customer"). FK to
 * `query_sources`; `code` is unique per parent source, not globally. See
 * ADR-0016 / ADR-0024.
 */

/** Slim parent reference embedded in list/detail rows. */
const QuerySourceRefSchema = z.object({ id: UuidSchema, code: z.string(), name: z.string() });

export const QuerySubSourceListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  querySource: QuerySourceRefSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type QuerySubSourceListItem = z.infer<typeof QuerySubSourceListItemSchema>;

export const QuerySubSourceListQuerySchema = PaginationQuerySchema.extend({
  querySourceId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type QuerySubSourceListQuery = z.infer<typeof QuerySubSourceListQuerySchema>;

export const QuerySubSourceListResponseSchema = paginatedResponseSchema(
  QuerySubSourceListItemSchema,
);
export type QuerySubSourceListResponse = z.infer<typeof QuerySubSourceListResponseSchema>;

export const QuerySubSourceDetailSchema = QuerySubSourceListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type QuerySubSourceDetail = z.infer<typeof QuerySubSourceDetailSchema>;

const QuerySubSourceCoreFieldsSchema = z.object({
  querySourceId: UuidSchema.describe('Parent query source UUID. Use GET /query-sources/options.'),
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateQuerySubSourceRequestSchema = QuerySubSourceCoreFieldsSchema;
export type CreateQuerySubSourceRequest = z.infer<typeof CreateQuerySubSourceRequestSchema>;

/** Partial: `querySourceId` may be re-pointed; `code` stays unique per source. */
export const UpdateQuerySubSourceRequestSchema = QuerySubSourceCoreFieldsSchema.partial();
export type UpdateQuerySubSourceRequest = z.infer<typeof UpdateQuerySubSourceRequestSchema>;
